import EqualizerIcon from '@mui/icons-material/Equalizer';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
    Box,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    useMediaQuery,
} from '@mui/material';
import React from 'react';
import { useGetRsvpSentStatsQuery } from '../../../../../app/Slices/SevenDayApiSlice';
import { openDashboardReportExportMenu } from '../../dashboardReportTableConfig';
import { StatsBreakdown, StatsRange } from '../../../../enums/reports';
import { CustomButton } from '../../../../../layout/styledcomponents/CustomButton';
import {
    dashboardReportBreakdownOptions,
    dashboardReportRangeOptions,
} from '../../dashboardPeriod';
import { useDashboardReportFilters } from '../../useDashboardReportFilters';
import RsvpsSentReportGraph from './RsvpsSentReportGraph';
import RsvpsSentReportList from './RsvpsSentReportList';
import RsvpsSentBookingsList from './RsvpsSentBookingsList';
import ReportTableTabs, { ReportTableTab } from '../../ReportTableTabs';

export default function RsvpsSentComponentPage() {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const [graphView, setGraphView] = React.useState(true);
    const [tableTab, setTableTab] = React.useState<ReportTableTab>('summary');
    const { range, breakdown, setRange, setBreakdown } =
        useDashboardReportFilters();

    const { data, isLoading } = useGetRsvpSentStatsQuery({
        range,
        breakdown,
        includeBookings: true,
    });

    return (
        <Grid sx={{ mt: 1 }}>
            <Grid
                sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between',
                    gap: 1.5,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: 1.5,
                        width: isMobile ? '100%' : 'auto',
                    }}
                >
                    <FormControl size='small' sx={{ minWidth: 170 }}>
                        <InputLabel id='rsvp-sent-range-label'>
                            Range
                        </InputLabel>
                        <Select
                            labelId='rsvp-sent-range-label'
                            value={range}
                            label='Range'
                            onChange={(event) =>
                                setRange(event.target.value as StatsRange)
                            }
                        >
                            {dashboardReportRangeOptions.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size='small' sx={{ minWidth: 170 }}>
                        <InputLabel id='rsvp-sent-breakdown-label'>
                            Breakdown
                        </InputLabel>
                        <Select
                            labelId='rsvp-sent-breakdown-label'
                            value={breakdown}
                            label='Breakdown'
                            onChange={(event) =>
                                setBreakdown(
                                    event.target.value as StatsBreakdown,
                                )
                            }
                        >
                            {dashboardReportBreakdownOptions.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {graphView ? (
                        <CustomButton
                            disableRipple
                            variant='outlined'
                            color='primary'
                            onClick={() => setGraphView(false)}
                        >
                            <FormatListBulletedIcon fontSize='small' />
                        </CustomButton>
                    ) : (
                        <>
                            <CustomButton
                                disableRipple
                                variant='outlined'
                                color='primary'
                                onClick={() => setGraphView(true)}
                            >
                                <EqualizerIcon fontSize='small' />
                            </CustomButton>
                            {tableTab === 'items' ? (
                                <CustomButton
                                    disableRipple
                                    variant='contained'
                                    color='neutral'
                                    onClick={openDashboardReportExportMenu}
                                >
                                    <FileDownloadIcon fontSize='small' />
                                </CustomButton>
                            ) : null}
                        </>
                    )}
                </Box>
            </Grid>

            <Typography sx={{ mt: 2 }} variant='h5'>
                {isLoading
                    ? 'Loading...'
                    : `RSVPs Sent: ${data?.totalSent ?? 0} | Not Sent: ${
                          data?.totalNotSent ?? 0
                      }`}
            </Typography>

            {graphView ? (
                <RsvpsSentReportGraph points={data?.points ?? []} />
            ) : (
                <ReportTableTabs
                    summaryLabel='By Period'
                    itemsLabel='Individual Guest Bookings'
                    onTabChange={setTableTab}
                    summaryContent={
                        <RsvpsSentReportList points={data?.points ?? []} />
                    }
                    itemsContent={
                        <RsvpsSentBookingsList
                            bookings={data?.bookings ?? []}
                        />
                    }
                />
            )}
        </Grid>
    );
}
